"use client";

import type { CSSProperties } from "react";

import { BRAND_COLORS, hexToRgba } from "@tami/tokens";
import { cn } from "../../cn";

export interface BioLinkRowProps {
  /** Numeração fantasma (`"01"`–`"04"`), texto e não decoração — a régua da lista vem das linhas se sucedendo. */
  readonly index: string;
  readonly label: string;
  /** Linha de apoio, quando o rótulo sozinho não diz o que a pessoa ganha. */
  readonly description?: string;
  readonly href: string;
  /** Destino fora do site: abre em outra aba e leva `rel` de segurança. */
  readonly external?: boolean;
  readonly className?: string;
}

/**
 * A linha da lista editorial de links da página `/links` (a da bio do
 * Instagram) — a direção escolhida (Task 6) troca o cartão-com-sombra por uma
 * lista numerada com régua, seta e sem sombra. Cada linha desenha só a própria
 * borda inferior; a borda de abertura da lista (acima do item 01) é
 * responsabilidade de quem empilha as linhas, não deste componente.
 *
 * As cores variam com o hover/foco (fundo, seta) mas o Tailwind do app não
 * enxerga classe montada em runtime a partir de uma constante — por isso elas
 * viram variável CSS (`--biolinkrow-*`) fixada uma vez no `style` do `<a>` e
 * lida por classes estáticas (`bg-[--biolinkrow-hover-bg]`), em vez de
 * interpolar o hex direto na classe.
 */
export function BioLinkRow({
  index,
  label,
  description,
  href,
  external = false,
  className,
}: BioLinkRowProps) {
  const externalProps = external
    ? { target: "_blank", rel: "noopener noreferrer" as const }
    : {};

  return (
    <a
      href={href}
      {...externalProps}
      className={cn(
        "group flex min-h-11 items-center gap-3.5 border-b border-[--biolinkrow-border] py-[19px]",
        "text-[--biolinkrow-ink] no-underline transition-colors duration-150",
        "hover:bg-[--biolinkrow-hover-bg]",
        "focus-visible:rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[--biolinkrow-ink]",
        "motion-reduce:transition-none",
        className,
      )}
      style={
        {
          "--biolinkrow-ink": BRAND_COLORS.ink,
          "--biolinkrow-border": hexToRgba(BRAND_COLORS.ink, 0.14),
          "--biolinkrow-hover-bg": hexToRgba(BRAND_COLORS.ink, 0.035),
          "--biolinkrow-num": hexToRgba(BRAND_COLORS.ink, 0.2),
          "--biolinkrow-arrow": hexToRgba(BRAND_COLORS.ink, 0.4),
          "--biolinkrow-desc": BRAND_COLORS.mutedBodyGray,
        } as CSSProperties
      }
    >
      {/*
        Decorativo: a ordem da lista já carrega a sequência para quem usa
        leitor de tela, e "zero um" antes de cada link não acrescenta
        informação. O HTML da direção não marca isso porque é protótipo de
        forma visual, não autoridade de acessibilidade.
      */}
      <span
        aria-hidden
        className="w-[38px] shrink-0 text-[32px] font-semibold leading-none text-[--biolinkrow-num]"
      >
        {index}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="text-[17px] font-semibold leading-[1.25]">{label}</span>
        {description ? (
          <span className="text-[13px] leading-[1.3] text-[--biolinkrow-desc]">
            {description}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden
        className="shrink-0 text-[18px] text-[--biolinkrow-arrow] transition-[color,transform] duration-150 group-hover:translate-x-0.5 group-hover:text-[--biolinkrow-ink] motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
      >
        →
      </span>
    </a>
  );
}
